import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, LocateFixed, Loader2, MapPin, AlertCircle } from 'lucide-react';
import { logWarn } from '@/lib/log';

/*
 * LocationPicker — free map-based location picker (no API key).
 * - Geocoding uses a resilient fallback chain: Photon (Komoot) -> Nominatim (OSM).
 * - Map is plain Leaflet (imperative) to avoid the react-leaflet StrictMode
 *   "Map container is already initialized" bug.
 * - Isolated so it can be swapped for Google Places later without touching callers.
 * value: { address, lat, lng }
 * onChange: (next) => void  where next = { address, lat, lng }
 */

const DEFAULT_CENTER = { lat: 28.5563, lng: 77.3253 }; // SRPS Filling Station, Sector 95, Noida
const PHOTON = 'https://photon.komoot.io';
const NOMINATIM = 'https://nominatim.openstreetmap.org';

const pinIcon = L.divIcon({
  className: 'location-picker-pin',
  html: '<div style="background:#0F4C81;width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35)"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 20],
});

// Build a readable one-line address from Photon feature properties.
const photonLabel = (p) => {
  const parts = [p.name, p.street, p.district, p.city || p.county, p.state, p.postcode, p.country]
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i);
  return parts.join(', ');
};

const fetchWithTimeout = async (url, ms = 8000) => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
  } finally {
    clearTimeout(t);
  }
};

// --- Forward geocoders (address -> coordinates) ---
const searchPhoton = async (q) => {
  const r = await fetchWithTimeout(`${PHOTON}/api/?q=${encodeURIComponent(q)}&limit=6&lang=en&lat=20.5937&lon=78.9629`);
  if (!r.ok) throw new Error('photon');
  const d = await r.json();
  return (d.features || []).map((f) => ({
    place_id: `p_${f.properties.osm_type || 'x'}${f.properties.osm_id || Math.random()}`,
    label: photonLabel(f.properties),
    lat: f.geometry.coordinates[1],
    lng: f.geometry.coordinates[0],
  }));
};
const searchNominatim = async (q) => {
  const r = await fetchWithTimeout(`${NOMINATIM}/search?format=json&addressdetails=1&limit=6&countrycodes=in&accept-language=en&q=${encodeURIComponent(q)}`);
  if (!r.ok) throw new Error('nominatim');
  const d = await r.json();
  return (Array.isArray(d) ? d : []).map((x) => ({
    place_id: `n_${x.place_id}`,
    label: x.display_name,
    lat: parseFloat(x.lat),
    lng: parseFloat(x.lon),
  }));
};

// --- Reverse geocoders (coordinates -> address) ---
const reversePhoton = async (la, ln) => {
  const r = await fetchWithTimeout(`${PHOTON}/reverse?lat=${la}&lon=${ln}&lang=en`);
  if (!r.ok) throw new Error('photon');
  const d = await r.json();
  const f = (d.features || [])[0];
  return f ? photonLabel(f.properties) : null;
};
const reverseNominatim = async (la, ln) => {
  const r = await fetchWithTimeout(`${NOMINATIM}/reverse?format=json&accept-language=en&lat=${la}&lon=${ln}`);
  if (!r.ok) throw new Error('nominatim');
  const d = await r.json();
  return d && d.display_name ? d.display_name : null;
};

// Dropdown under the search box: result list plus empty/unavailable states.
const ResultsDropdown = ({ results, error, testId, onSelect, onRetry }) => (
  <div className="absolute z-[400] mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-56 overflow-auto" data-testid={`${testId}-results`}>
    {results.map((r) => (
      <button
        type="button"
        key={r.place_id}
        onClick={() => onSelect(r)}
        className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted"
        data-testid={`${testId}-result`}
      >
        <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
        <span className="line-clamp-2">{r.label}</span>
      </button>
    ))}
    {results.length === 0 && error === 'empty' && (
      <div className="px-3 py-3 text-sm text-muted-foreground" data-testid={`${testId}-empty`}>No matches. Try a different search, or click on the map to drop a pin.</div>
    )}
    {results.length === 0 && error === 'unavailable' && (
      <div className="px-3 py-3 text-sm flex items-start gap-2" data-testid={`${testId}-error`}>
        <AlertCircle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
        <span>Search is busy right now. <button type="button" className="underline text-primary" onClick={onRetry}>Retry</button> or click on the map to set the location.</span>
      </div>
    )}
  </div>
);

export const LocationPicker = ({ value, onChange, testId = 'location-picker', initialQuery = '' }) => {
  const lat = value?.lat || 0;
  const lng = value?.lng || 0;
  const hasPin = !!(lat && lng);

  // Prefill the search box (e.g. with the customer's address text) so the
  // debounced search kicks in immediately when used for pin cleanup.
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);       // 'unavailable' | 'empty' | null
  const [locating, setLocating] = useState(false);

  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  // Reverse geocode with fallback; always keep coordinates even if names fail.
  const reverse = async (la, ln) => {
    onChangeRef.current({ ...(valueRef.current || {}), lat: la, lng: ln });
    let label = null;
    try { label = await reversePhoton(la, ln); } catch (e) { logWarn('Photon reverse geocode failed, trying Nominatim', e); }
    if (!label) { try { label = await reverseNominatim(la, ln); } catch (e) { logWarn('Nominatim reverse geocode failed, keeping coordinates', e); } }
    if (label) onChangeRef.current({ address: label, lat: la, lng: ln });
  };

  // Forward geocode with fallback + explicit user feedback.
  const runSearch = async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); setError(null); return; }
    setSearching(true);
    setError(null);
    let list = [];
    let reached = false;
    try { list = await searchPhoton(q); reached = true; } catch (e) { logWarn('Photon search failed, trying Nominatim', e); }
    if (!list.length) {
      try { list = await searchNominatim(q); reached = true; } catch (e) { logWarn('Nominatim search failed', e); }
    }
    setSearching(false);
    setResults(list);
    setOpen(true);
    if (!reached) setError('unavailable');
    else if (!list.length) setError('empty');
    else setError(null);
  };

  // Debounced search as the user types.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) { setResults([]); setError(null); return undefined; }
    debounceRef.current = setTimeout(() => runSearch(query), 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Initialise the Leaflet map exactly once (imperative — StrictMode safe).
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return undefined;
    const start = hasPin ? [lat, lng] : [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng];
    const map = L.map(containerRef.current, { scrollWheelZoom: false }).setView(start, hasPin ? 15 : 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(map);
    map.on('click', (e) => reverse(e.latlng.lat, e.latlng.lng));
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync marker + view whenever the coordinates change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (hasPin) {
      if (!markerRef.current) {
        markerRef.current = L.marker([lat, lng], { icon: pinIcon, draggable: true }).addTo(map);
        markerRef.current.on('dragend', (e) => { const p = e.target.getLatLng(); reverse(p.lat, p.lng); });
      } else {
        markerRef.current.setLatLng([lat, lng]);
      }
      map.setView([lat, lng], Math.max(map.getZoom(), 15));
    } else if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  const selectResult = (r) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    setError(null);
    onChange({ address: r.label, lat: r.lat, lng: r.lng });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { reverse(pos.coords.latitude, pos.coords.longitude); setLocating(false); },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {/* Search box (OpenStreetMap powered for now; swappable to Google) */}
      <div className="relative" ref={wrapRef}>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search location (area, city, landmark)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => (results.length || error) && setOpen(true)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (debounceRef.current) clearTimeout(debounceRef.current); runSearch(query); } }}
              data-testid={`${testId}-search`}
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <Button type="button" variant="secondary" className="h-10 shrink-0" onClick={() => runSearch(query)} disabled={searching || query.trim().length < 2} data-testid={`${testId}-search-button`}>
            <Search className="h-4 w-4" />
          </Button>
          <Button type="button" variant="secondary" className="h-10 shrink-0" onClick={useMyLocation} disabled={locating} data-testid={`${testId}-locate`} title="Use my current location">
            {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
          </Button>
        </div>
        {open && (results.length > 0 || error) && (
          <ResultsDropdown results={results} error={error} testId={testId}
            onSelect={selectResult} onRetry={() => runSearch(query)} />
        )}
      </div>

      {/* Interactive map: click or drag the pin to set the exact spot */}
      <div ref={containerRef} className="rounded-xl border border-border overflow-hidden bg-muted" style={{ height: '200px' }} data-testid={`${testId}-map`} />
      <p className="text-[11px] text-muted-foreground">{hasPin ? `Pinned at ${lat.toFixed(5)}, ${lng.toFixed(5)} — drag the pin or click the map to adjust.` : 'Search above or click on the map to drop a pin.'}</p>
    </div>
  );
};

export default LocationPicker;
